/**
 * Integration configuration - data-driven definitions for all available integrations
 * This allows adding new integrations by configuration instead of hardcoding JSX
 */

import { IntegrationProvider, IntegrationProduct, IntegrationStatus } from "./types";

/**
 * All available integrations
 * Each integration can define:
 * - id: unique identifier
 * - name: display name
 * - description: user-facing description
 * - icon: emoji icon
 * - provider: provider type (e.g., "ZOHO")
 * - product: product within provider (e.g., "BOOKS", "PROJECTS")
 * - category: grouping category
 * - connectable: whether backend supports connecting this product
 * - supportsDisconnect: whether disconnect is supported
 * - backendIntegrationName: backend identifier pattern
 */
export const integrationDefinitions = [
    // === ZOHO BOOKS ===
    {
        id: "zoho-books",
        name: "Zoho Books",
        description: "Import accounting records and financial statements",
        icon: "📊",
        provider: IntegrationProvider.ZOHO,
        product: IntegrationProduct.BOOKS,
        category: "ERP",
        connectable: true,
        supportsDisconnect: true,
        backendIntegrationName: `INTEGRATION#${IntegrationProvider.ZOHO}#${IntegrationProduct.BOOKS}`,
    },
    // === ZOHO PROJECTS ===
    {
        id: "zoho-projects",
        name: "Zoho Projects",
        description: "Connect your Zoho Projects workspace to allow MiFlow to access projects, tasks, milestones, timesheets and project analytics",
        icon: "📋",
        provider: IntegrationProvider.ZOHO,
        product: IntegrationProduct.PROJECTS,
        category: "ERP",
        connectable: true,
        supportsDisconnect: true,
        backendIntegrationName: `INTEGRATION#${IntegrationProvider.ZOHO}#${IntegrationProduct.PROJECTS}`,
    },
    // === QUICKBOOKS ===
    {
        id: "quickbooks",
        name: "QuickBooks",
        description: "Sync invoices, expenses, and financial data in real-time",
        icon: "💼",
        provider: "QUICKBOOKS",
        product: null,
        category: "ERP",
        connectable: false,
        supportsDisconnect: false,
    },
    // === ODOO ===
    {
        id: "odoo",
        name: "Odoo",
        description: "Connect your ERP and CRM data for comprehensive insights",
        icon: "♦️",
        provider: "ODOO",
        product: null,
        category: "ERP",
        connectable: false,
        supportsDisconnect: false,
    },
    // === XERO ===
    {
        id: "xero",
        name: "Xero",
        description: "Cloud accounting integration with automatic sync",
        icon: "💙",
        provider: "XERO",
        product: null,
        category: "ERP",
        connectable: false,
        supportsDisconnect: false,
    },
    // === HOLDED ===
    {
        id: "holded",
        name: "Holded",
        description: "Business management platform with invoicing and projects",
        icon: "🟣",
        provider: "HOLDED",
        product: null,
        category: "ERP",
        connectable: false,
        supportsDisconnect: false,
    },
];

/**
 * Get integration definition by ID
 * @param {string} id
 * @returns {Object|null}
 */
export function getIntegrationById(id) {
    return integrationDefinitions.find(def => def.id === id) || null;
}

/**
 * Get integration definition by provider and product
 * @param {string} provider
 * @param {string} [product]
 * @returns {Object|null}
 */
export function getIntegrationByProviderAndProduct(provider, product) {
    return integrationDefinitions.find(
        def => def.provider === provider && def.product === product
    ) || null;
}

/**
 * Get first N integrations for initial display
 * @param {number} count - Number to return
 * @returns {Array}
 */
export function getInitialIntegrations(count = 3) {
    return integrationDefinitions.slice(0, count);
}

/**
 * Get remaining integrations after initial display
 * @param {number} skipCount - Number to skip
 * @returns {Array}
 */
export function getRemainingIntegrations(skipCount = 3) {
    return integrationDefinitions.slice(skipCount);
}

/**
 * Group integrations by category
 * @returns {Object} - Object with category keys and integration arrays as values
 */
export function groupIntegrationsByCategory() {
    return integrationDefinitions.reduce((acc, integration) => {
        const category = integration.category || "Other";
        if (!acc[category]) {
            acc[category] = [];
        }
        acc[category].push(integration);
        return acc;
    }, {});
}
